// webcomponent.ts
import { ASS } from "@adornis/ass/index.js";
import { SignalWatcher } from "@lit-labs/preact-signals";
import {
  adoptStyles,
  LitElement,
  noChange
} from "lit";
import { Directive, directive } from "lit/directive.js";
import { html as staticHtml, unsafeStatic } from "lit/static-html.js";
import { stateful } from "./stateful.js";
var tailwindSheet = new Promise((resolve) => {
  const tailwindLink = document.head?.querySelector("[data-tailwind]");
  if (!tailwindLink) {
    console.error("Could not find the tailwind link tag in the head of the document");
    return resolve(new CSSStyleSheet());
  }
  const createConstructedStylesheet = (sourceSheet) => {
    const constructedSheet = new CSSStyleSheet();
    for (const rule of Array.from(sourceSheet.cssRules)) {
      constructedSheet.insertRule(rule.cssText, constructedSheet.cssRules.length);
    }
    return constructedSheet;
  };
  if (tailwindLink.sheet) {
    resolve(createConstructedStylesheet(tailwindLink.sheet));
  } else {
    tailwindLink.onload = () => resolve(createConstructedStylesheet(tailwindLink.sheet));
  }
});
var setProps = directive(
  class extends Directive {
    render(params, propDefs) {
      return noChange;
    }
    update(_part, [params, propDefs]) {
      let shouldForceUpdate = false;
      for (const [propName, propValue] of Object.entries(params ?? {})) {
        const additionalInformation = propDefs?.[propName];
        if (additionalInformation?.attribute)
          _part.element.setAttribute(
            typeof additionalInformation.attribute === "boolean" ? propName : additionalInformation.attribute,
            propValue
          );
        else {
          _part.element[propName] = propValue;
        }
        if (!propDefs?.[propName])
          shouldForceUpdate = true;
      }
      if (shouldForceUpdate)
        _part.element.requestUpdate();
      return this.render(params, propDefs);
    }
  }
);
function webcomponent(tagName, renderFn, options) {
  if (options?.styles && !options.shadowRoot)
    throw new Error(
      "functional-lit::webcomponent::options.styles can't be set without options.shadowRoot -> " + tagName
    );
  class FunctionalElement extends SignalWatcher(LitElement) {
    constructor() {
      super(...arguments);
      // store this to make sure it's the same function reference, otherwise stateful can't detect this is the same component
      this.renderFn = () => renderFn.bind(this)(this);
      this.inlineStyleParts = /* @__PURE__ */ new Set();
      this._liteElementStyleSheets = [];
    }
    static {
      this.properties = options?.properties ?? {};
    }
    createRenderRoot() {
      return !options?.shadowRoot ? this : this.attachShadow({ mode: "open" });
    }
    connectedCallback() {
      super.connectedCallback();
      options?.connectedCallback?.();
    }
    disconnectedCallback() {
      super.disconnectedCallback();
      options?.disconnectedCallback?.();
    }
    async updated(changedProperties) {
      super.updated(changedProperties);
      this._updateStyles();
    }
    render() {
      return stateful(this.renderFn)();
    }
    // TODO This is just for backwards compatibility reasons to allow LiteElements inside webcomponents and allow acss directive
    // #region "BACKWARDS COMPATIBILITY"
    /**
     * @deprecated only for internal use
     */
    async _updateStyles() {
      adoptStyles(this.renderRoot, [
        ...this._liteElementStyleSheets.map((styleFn) => styleFn()).flat().map((style) => ASS.makeSheet(style).toSheet()),
        ...options?.styles ? [ASS.makeSheet(options?.styles).toSheet()] : [],
        ...new Set([...this.inlineStyleParts].map((part) => part.sheet).filter(Boolean)),
        await tailwindSheet
      ]);
    }
    addLiteElementStyleSheet(styleSheetFn) {
      if (this._liteElementStyleSheets.includes(styleSheetFn))
        return;
      this._liteElementStyleSheets.push(styleSheetFn);
      if (this.hasUpdated)
        this._updateStyles();
    }
    removeLiteElementStyleSheet(styleSheet) {
      const styleSheetIndex = this._liteElementStyleSheets.indexOf(styleSheet);
      if (styleSheetIndex === -1)
        return;
      this._liteElementStyleSheets.splice(styleSheetIndex, 1);
    }
    // #endregion
  }
  customElements.define(tagName, FunctionalElement);
  return {
    elementDefinition: FunctionalElement,
    render(props) {
      return staticHtml` <${unsafeStatic(tagName)} ${setProps(
        props ?? void 0,
        options?.properties
      )}></${unsafeStatic(tagName)}> `;
    }
  };
}
export {
  webcomponent
};
